import {
  COMPONENT_DOCS_URLS,
  Tenant,
  getEnvironment,
  useAuthFetch,
  useHasRole,
  useNavigateWithSearchParams,
  useUser,
} from "@p0security/frontend-core";
import {
  ErrorBoundaryDisplay,
  ErrorDisplay,
  MaturityTag,
  useFirestoreDoc,
} from "@p0security/frontend-core/react";
import {
  isAvailableInEnvironment,
  visibleComponentEntries,
} from "@p0security/shared/install/options";
import type {
  ConfigOf,
  InstallSpec,
  ItemComponent,
  ItemConfigOf,
} from "@p0security/shared/install/types";
import { isNewConfig } from "@p0security/shared/integrations/shared";
import type { RegisteredComponentIntegration } from "@p0security/shared/integrations/types";
import type { ColumnsType } from "@p0security/ui";
import { Button, Space, Spin, Table, Typography } from "@p0security/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { pickBy, sortBy } from "lodash";
import pluralize from "pluralize";
import type { ComponentProps } from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router";

import type { FrontendInstaller, InstallSiblings } from "../types";
import { Component } from "./Component";
import { configPath } from "./Config";
import { Root } from "./Root";

export type InstallProps<T extends InstallSpec> = {
  cardOverride?: Partial<ComponentProps<InstallSiblings["IntegrationCard"]>>;
  componentOverride?: keyof T & string;
  installer: FrontendInstaller<T>;
  integration: string;
  onInstall?: (config: ConfigOf<T>) => void;
  registration: RegisteredComponentIntegration<T>;
  /** List-page components/data injected by `frontend` — see InstallSiblings */
  siblings: InstallSiblings;
};

type ComponentDatum<T> = {
  component: ItemComponent<any>;
  key: string;
  items: ItemConfigOf<ConfigOf<T>[keyof T]>[];
};

const installColumns = <T extends InstallSpec>(
  components: T,
  itemKind: RegisteredComponentIntegration<T>["itemKind"]
): ColumnsType<ComponentDatum<T>> => [
  {
    key: "label",
    title: "Available components",
    render: (_, { component }) => (
      <Space align="center" size="small">
        <Button type="link" size="small" className="button-link">
          {component.label}
        </Button>
        {component.maturity !== "ga" && (
          <MaturityTag maturity={component.maturity} />
        )}
      </Space>
    ),
  },
  {
    key: "items",
    title: "Installed?",
    width: "125px",
    render: (_, { component, items, key }) => {
      const inProgress = (items ?? []).filter((i) => i.state !== "installed");
      return items?.length
        ? component.type === "singleton"
          ? inProgress.length
            ? "In progress"
            : "Installed"
          : `${items.length} ${pluralize(typeof itemKind === "function" ? itemKind(key) : itemKind, items.length)}${
              inProgress.length ? ` (${inProgress.length} in progress)` : ""
            }`
        : "Not installed";
    },
  },
  {
    key: "use",
    title: "Use",
    render: (_, { component }) => component.description,
  },
];

const ComponentSelect = <T extends InstallSpec>({
  config,
  registration,
}: InstallProps<T> & { config: ConfigOf<T> }) => {
  const { components, itemKind } = registration;
  const navigate = useNavigateWithSearchParams();
  const flags = useFlags();

  const columns = useMemo(
    () => installColumns(components, itemKind),
    [components, itemKind]
  );

  const data = useMemo(() => {
    const data = visibleComponentEntries(
      components,
      getEnvironment().environmentType,
      flags
    ).map(([key, component]) => ({
      component,
      key,
      items: Object.values(config?.[key] ?? []),
    })) as any as ComponentDatum<T>[];
    return sortBy(data, ({ component }) => component.label);
  }, [components, config, flags]);

  const getRowEvents = useCallback(
    (record: ComponentDatum<T>) => ({
      onClick: () => {
        navigate(`./${record.key}`);
      },
    }),
    [navigate]
  );

  return (
    <Table<ComponentDatum<T>>
      className="table-styles"
      columns={columns}
      dataSource={data}
      pagination={{ position: ["none", "none"] as any }}
      onRow={getRowEvents}
      rowClassName="enabled-row"
    />
  );
};

/** The top-level entry point for componentized integration installs
 *
 * Each component is rendered as a row in a top-level table. Components
 * are rendered according to the integration's component schema using the
 * {@link Component} React component.
 */
// TODO: flatten table w.r.t. installed items
export const Install = <T extends Record<string, ItemComponent<any>>>(
  props: InstallProps<T>
) => {
  const { cardOverride, integration, installer, registration, siblings } =
    props;
  const { IntegrationCard, IntegrationsContext, OwnerAlert, gaIntegrations } =
    siblings;
  const { label: title, components, logoUrl: logo } = registration;
  const { user } = useUser();
  const [error, setError] = useState<string>();
  const [warning, setWarning] = useState<string>();
  const [isFetching, setIsFetching] = useState(false);
  const authFetch = useAuthFetch(setError, setIsFetching);
  const { component: componentFromProps } = useParams<{ component: string }>();
  const tenantId = useContext(Tenant);
  const { orgSlug } = useParams();
  const { orgData } = useUser();
  const canEdit = useHasRole("owner");
  const flags = useFlags();
  // Used to provide data for `otherInstalled` `optionProvider`s
  const installed = useContext(IntegrationsContext);

  const component = useMemo(
    () => props.componentOverride ?? componentFromProps,
    [componentFromProps, props.componentOverride]
  );

  // Components unavailable in this environment are treated as nonexistent,
  // so that navigating directly to one redirects to the integration page
  const hasComponent =
    !!component &&
    component in components &&
    isAvailableInEnvironment(
      components[component].maturity,
      getEnvironment().environmentType
    );

  const integrationPath = useMemo(
    () => `o/${tenantId}/integrations/${integration}`,
    [integration, tenantId]
  );

  const integrationDoc = useFirestoreDoc<ConfigOf<T>>(integrationPath, {
    live: true,
  });

  const onRemove = useCallback(async () => {
    await authFetch(configPath({ integration }), {
      method: "DELETE",
    });
  }, [authFetch, integration]);

  const clearError = useCallback(() => setError(undefined), []);
  const clearWarning = useCallback(() => setWarning(undefined), []);

  // A doc reload will cancel any in-progress authFetch hooks; potentially
  // preventing isFetching from being reset
  useEffect(() => {
    setIsFetching(false);
  }, [integrationDoc.doc?.data]);

  const config = integrationDoc.doc?.data;

  const context = useMemo(
    () => ({
      ...getEnvironment(),
      authFetch,
      config,
      installed: pickBy(installed, isNewConfig),
      orgData: orgData && orgSlug ? { ...orgData, slug: orgSlug } : undefined,
      tenantId,
      user,
      featureFlags: flags,
    }),
    [authFetch, config, installed, orgData, orgSlug, tenantId, user, flags]
  );

  const componentDocsUrl = useMemo(() => {
    if (component) {
      return COMPONENT_DOCS_URLS[`${integration}/${component}`];
    }
    return gaIntegrations[integration]?.docs.install;
  }, [integration, component, gaIntegrations]);
  return !component || hasComponent ? (
    <IntegrationCard
      title={title}
      integration={integration}
      component={component}
      componentTitle={component && components[component].label}
      canRemove={!!integrationDoc.doc && !component}
      integrationDoc={integrationDoc.doc}
      logo={logo}
      onRemove={onRemove}
      hideParentDocsLink
      {...cardOverride}
    >
      <ErrorBoundaryDisplay>
        {integrationDoc.loading ? (
          <Spin />
        ) : (
          <div>
            {error && (
              <ErrorDisplay
                title={`Error configuring ${title}`}
                error={error}
                onClose={clearError}
                data-testid="error-display"
              />
            )}
            {warning && (
              <ErrorDisplay
                type="warning"
                title={`Warning configuring ${title}`}
                error={warning}
                onClose={clearWarning}
                data-testid="warning-display"
              />
            )}
            {!integrationDoc.doc?.data.root &&
            "root" in installer.components &&
            "root" in components ? (
              canEdit ? (
                <Root
                  authFetch={authFetch}
                  context={context}
                  isFetching={isFetching}
                  {...props}
                />
              ) : (
                <OwnerAlert />
              )
            ) : (
              <div data-testid="install-inner-div">
                {hasComponent ? (
                  <Component
                    {...props}
                    authFetch={authFetch}
                    canEdit={canEdit}
                    config={config}
                    context={context}
                    componentKey={component}
                    docsUrl={componentDocsUrl}
                    error={error}
                    installer={installer.components}
                    isFetching={isFetching}
                    onWarning={setWarning}
                  />
                ) : (
                  <>
                    {"root" in components && (
                      <Root
                        authFetch={authFetch}
                        context={context}
                        isFetching={isFetching}
                        {...props}
                        disabled
                      />
                    )}
                    {props.installer.preamble}
                    <Typography.Paragraph>
                      To install, please select a component below:
                    </Typography.Paragraph>
                    <ComponentSelect<T> {...props} config={config} />
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </ErrorBoundaryDisplay>
    </IntegrationCard>
  ) : (
    <Navigate to=".." relative="path" replace />
  );
};
